// Comprobantes (factura, recibo, vale, boleta): adjuntar desde el celular y mostrar su estado.
// Los archivos financieros nuevos se conservan; los ya eliminados no se recuperan.
import React, { useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { useSucursalActiva } from '../../context/SucursalActivaContext';
import { useProtectedFileUrl } from '../../hooks/useProtectedFileUrl';
import { getToken } from '../../services/api';
import { fechaGT, formatDate } from '../../utils/dates';
import './ui.css';

const MAX_BYTES = 10 * 1024 * 1024;
const TIPOS_OK = /^image\/|^application\/pdf$/;
const TIPOS_EVIDENCIA = /^image\/(jpeg|png|webp)$/; // evidencia del cuadre: solo imágenes

/** Reduce fotos grandes del celular (máx. 1600 px, JPEG) para que suban rápido. Si no se puede, deja el original. */
export async function comprimir(file) {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size < 600 * 1024) return file;
  try {
    const bmp = await createImageBitmap(file);
    const k = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    const c = document.createElement('canvas');
    c.width = Math.round(bmp.width * k); c.height = Math.round(bmp.height * k);
    c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
    const blob = await new Promise((r) => c.toBlob(r, 'image/jpeg', 0.82));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], (file.name || 'comprobante').replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch { return file; }
}

/**
 * Íconos compactos: 📷 abre la cámara; 📎 abre galería/archivos; con adjunto, ✓ Adjuntado, Ver, reemplazar y ✕ quitar.
 * `archivo` es el File elegido (aún sin subir); `url` el comprobante ya guardado.
 * `soloImagen`: evidencia del cuadre: solo cámara, con miniatura y sin quitar lo guardado. `requerido`: muestra "Pendiente de foto".
 */
export function AdjuntarComprobante({ archivo, url, onChange, disabled, soloImagen = false, requerido = false, ayuda = 'Foto de la factura, recibo, vale o comprobante de pago.' }) {
  // El cajero solo ve los botones; la ayuda y la conservación son información del administrador.
  const { isAdmin } = useSucursalActiva() || {};
  const camara = useRef(null);
  const galeria = useRef(null);
  const [preview, setPreview] = useState('');
  const guardada = useProtectedFileUrl(soloImagen && !archivo ? url : '');
  useEffect(() => {
    if (!archivo || !/^image\//.test(archivo.type)) { setPreview(''); return undefined; }
    const u = URL.createObjectURL(archivo);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [archivo]);

  const elegir = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // permite volver a elegir el mismo archivo
    if (!f) return;
    if (!(soloImagen ? TIPOS_EVIDENCIA : TIPOS_OK).test(f.type)) return Swal.fire('Comprobante', soloImagen ? 'Usa una imagen JPG, PNG o WEBP.' : 'Usa una foto o un PDF.', 'warning');
    const listo = await comprimir(f);
    if (listo.size > MAX_BYTES) return Swal.fire('Comprobante', 'El archivo supera 10 MB.', 'warning');
    onChange(listo);
  };

  if (disabled) return url ? <ComprobanteLink url={url} /> : null;
  const tieneAlgo = !!archivo || !!url;
  // Acciones compactas con íconos (misma cámara y galería de siempre): 📷 tomar foto · 📎 adjuntar / reemplazar · ✕ quitar.
  return (
    <div className="fx-adjunto">
      <input ref={camara} type="file" accept="image/*" capture="environment" hidden onChange={elegir} />
      {!soloImagen && <input ref={galeria} type="file" accept="image/*,application/pdf" hidden onChange={elegir} />}
      {archivo && (preview
        ? <a href={preview} target="_blank" rel="noreferrer" title="Ver vista previa"><img className="fx-adjunto-mini" src={preview} alt="Vista previa del comprobante" /></a>
        : <span className="fx-adjunto-icono" aria-hidden="true">📄</span>)}
      {!archivo && guardada.url && <a href={guardada.url} target="_blank" rel="noreferrer" title="Ver comprobante"><img className="fx-adjunto-mini" src={guardada.url} alt="Comprobante guardado"
        onError={(e) => { e.currentTarget.style.display = 'none'; }} /></a>}
      {requerido && !tieneAlgo && <small className="fx-adjunto-falta">Pendiente de foto</small>}
      {tieneAlgo && <span className="fx-adjunto-ok">✓ Adjuntado{url && !archivo && <> · <ComprobanteLink url={url} texto="Ver" /></>}</span>}
      <span className="fx-adjunto-iconos">
        <button type="button" className="fx-adjunto-icono-btn" title={tieneAlgo ? 'Reemplazar con una foto' : 'Tomar foto'} aria-label={tieneAlgo ? 'Reemplazar con una foto' : 'Tomar foto'}
          onClick={() => camara.current?.click()}><span className="fx-adjunto-ico" aria-hidden="true">📷</span><small>{soloImagen && tieneAlgo ? 'Volver a tomar' : 'Tomar foto'}</small></button>
        {!soloImagen && <button type="button" className="fx-adjunto-icono-btn" title={tieneAlgo ? 'Reemplazar con una imagen' : 'Adjuntar imagen o comprobante'} aria-label={tieneAlgo ? 'Reemplazar con una imagen' : 'Adjuntar imagen o comprobante'}
          onClick={() => galeria.current?.click()}><span className="fx-adjunto-ico" aria-hidden="true">📎</span><small>Adjuntar foto</small></button>}
        {tieneAlgo && (!soloImagen || archivo) && <button type="button" className="fx-adjunto-icono-btn fx-adjunto-quitar" title="Quitar" aria-label="Quitar adjunto" onClick={() => onChange(null)}><span className="fx-adjunto-ico" aria-hidden="true">✕</span><small>Quitar</small></button>}
      </span>
      {!tieneAlgo && isAdmin && <small className="fx-adjunto-ayuda">{ayuda} El comprobante financiero se conserva.</small>}
    </div>
  );
}

const cache = new Map();
/** Enlace al comprobante o aviso de que se eliminó por la política de retención. */
export function ComprobanteLink({ url, texto = 'Ver comprobante' }) {
  const { isAdmin, sucursalId } = useSucursalActiva() || {};
  const protegido = useProtectedFileUrl(url);
  const [info, setInfo] = useState(() => cache.get(url) || null);
  useEffect(() => {
    if (!url || cache.has(url)) return;
    let vivo = true;
    const headers = { Authorization: `Bearer ${getToken()}` };
    if (sucursalId && sucursalId !== 'all') headers['X-Sucursal-Activa'] = sucursalId;
    fetch(`${url}/info`, { headers }).then((r) => (r.ok ? r.json() : null)).then((j) => {
      const d = j?.data || { eliminado: false };
      cache.set(url, d);
      if (vivo) setInfo(d);
    }).catch(() => {});
    return () => { vivo = false; };
  }, [url, sucursalId]);
  if (!url) return null;
  if (info?.eliminado) {
    return <span className="fx-adjunto-eliminado" title={isAdmin && info.eliminadoAt ? `Eliminado el ${formatDate(fechaGT(info.eliminadoAt))}` : undefined}>
      {isAdmin ? 'Comprobante eliminado por la política anterior' : 'Comprobante no disponible'}</span>;
  }
  if (protegido.error) return <span className="fx-adjunto-eliminado">Comprobante no disponible</span>;
  return protegido.url ? <a href={protegido.url} target="_blank" rel="noreferrer">{texto}</a> : <span>Cargando comprobante…</span>;
}
